
import { Component, Vue, Watch } from "vue-property-decorator"

import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"

import api from "@/services/api"

import { Schedule, ScheduleFreq, FolderSchedules, SortByOptions, ScheduleStatus } from "@/types/schedule"
import cloneDeep from "lodash/cloneDeep"
import { CaseColumn } from "@/types/caseColumns"
import { TemplateDetails, TimeZone, Team } from "@/types"

import CalendarView from "./CalendarView.vue"
import ScheduleSettings from "./ScheduleSettings.vue"
import ScheduleEditService from "@/services/scheduleEditService"

import apiScheduleSettings from "@/services/api_ScheduleSettings"

import moment from "moment"
import { CurrentUser } from "@/types/user"
import { Getter, State } from "vuex-class"
import UserImage from "@/components/UserImage.vue"
import { FolderType, Folder } from "@/types/folder"
import _, { get } from "lodash"
import { UserSettings } from "@/types/userSettings"

@Component({
	components: {
		"v-select": vSelect,
		scrollbar: VuePerfectScrollbar,
		"data-table": DataTable,
		skeleton: SkeletonLoader,
		"calendar-view": CalendarView,
		"schedule-settings": ScheduleSettings,
		"user-image": UserImage
	}
})
export default class ScheduleSetup extends Vue {
	$refs!: {
		dataTable: DataTable
	}

	@State tenantSettings!: { [key: string]: any } | null

	@Getter("getUserSettings")
	public userSettings!: UserSettings

	@Getter("getMe")
	public me!: CurrentUser;

	private schedules: Schedule[] = [];
	private folderScheduleGroups: FolderSchedules[] = [];

	private loading: boolean = true
	private templates: TemplateDetails[] = [];
	private fields: CaseColumn[] = [];
	private generatingCase: boolean = false;

	private timeZones: TimeZone[] = []

	private showCalendar: boolean = false

	private showSettings: boolean = false

	private folders: { [title:string]:Folder } = {}
	private teams: Team[] = []

	// If filterOutCaseColumns is updated please also update the ScheduleService Api
	private filterOutCaseColumns = ["created", "closed", "openForDays", "isClosed", "templateId", "caseNumber", "lastUpdated", "hiddenFromInsights", "deleted"]
	public selectedFields : any[] = [];

	private scheduleEditService = new ScheduleEditService()

	private sortByOptions: SortByOptions[] = this.getDefaultSortByOptions()

	private getDefaultSortByOptions () : SortByOptions[] {
		return [
			{
				columnKey: "status",
				descending: "asc"
			},
			{
				columnKey: "title",
				descending: "asc"
			}
		]
	}

	private get scheduleFreqOptions () {
		return [
			{
				title: "Once",
				id: ScheduleFreq.Once
			},
			{
				title: "Daily",
				id: ScheduleFreq.Daily
			},
			{
				title: "Weekly",
				id: ScheduleFreq.Weekly
			},
			{
				title: "Monthly",
				id: ScheduleFreq.Monthly
			}
		]
	}

	public get hasSettingsAuthorization () : boolean {
		return this.me && !!this.me.isAdmin
	}

	private async loadFolders () {
		const refreshedFolders: Folder[] = await api.foldersGet(FolderType.Schedule)
		refreshedFolders.forEach(refreshedFolder => {
			const existingFolder = this.folders[refreshedFolder.title]
			if (existingFolder) {
				refreshedFolder.expanded = existingFolder.expanded
			} else {
				refreshedFolder.expanded = this.foldersExpandedByDefault
			}
		})

		this.folders = refreshedFolders.toDictionary("title")
	}

	private folderDeleted (folder: Folder) {
		const folderSchedules = this.schedules.filter(schedule => schedule.folder === folder.title)
		folderSchedules.forEach(schedule => schedule.folder = null)
	}

	private async loadTeams () {
		const teams: Team[] = await api.teamsGet()
		this.teams = teams
	}

	private get scheduleColumns () {
		if (!this.scheduleEditService) return []
		return this.scheduleEditService.columns.map((c: any) => {
			if (c.key === "timeZoneId") {
				if (this.tenantSettings && this.tenantSettings["timeZones"] && this.tenantSettings["timeZones"].length > 0) {
					const colValue = cloneDeep(c)
					colValue.options = colValue.options
						? colValue.options.filter((tz: TimeZone) => this.tenantSettings!["timeZones"].includes(tz.id))
						: colValue.options

					return colValue
				}
			}
			return c
		}).filter((c: any) => {
			if (c.key === "endTime") {
				return !this.tenantSettings || !this.tenantSettings["scheduleHideTimeSpan"]
			}
			if (c.key === "bulkCreateForTeams") {
				return !this.tenantSettings || !this.tenantSettings["scheduleHideBulkCreate"]
			}
			return true
		})
	}

	private async mounted () {
		await this.load()
	}

	private async load () {
		this.loading = true

		await this.scheduleEditService.load(this.me)
		await this.loadFolders()
		await this.loadTeams()
		await this.loadSchedules()
		await this.loadScheduleDefaults()

		this.loading = false
	}

	private async loadSchedules (): Promise<void> {
		const schedules = await api.retrieveSchedules()
		schedules.forEach((schedule: Schedule) => {
			schedule.status = this.determineScheduleStatus(schedule)
		})
		this.schedules = schedules

		const permittedFolders = this.scheduleEditService.getPermittedFolders()

		const folderScheduleGroups = _(this.schedules)
			.filter(c => c.folder === null || c.folder === undefined || permittedFolders[(c.folder)] !== undefined)
			.groupBy(c => c.folder)
			.map((groupedSchedules: Schedule[], folderName: string) => {
				const hasNoFolder = folderName === null || folderName === undefined || folderName === "null"

				const folderSchedules: FolderSchedules = {
					folderTitle: hasNoFolder ? "Uncategorized" : folderName,
					expanded: hasNoFolder || !!this.folders[folderName].expanded, // schedules with no folder are always expanded
					isFolderType: true,
					schedules: groupedSchedules,
					allowRowClick: true,
					onlyEmitOnRowClick: true
				}
				return folderSchedules
			}).value()

		this.folderScheduleGroups = folderScheduleGroups
	}

	public get foldersExpandedByDefault () {
		if (this.userSettings &&
			this.userSettings.settings &&
			this.userSettings.settings["scheduleFoldersExpanded"] !== null &&
			this.userSettings.settings["scheduleFoldersExpanded"] !== undefined) {
			return !!this.userSettings.settings["scheduleFoldersExpanded"]
		}
		return true
	}

	private loadScheduleItem (schedule : any) {
		let scheduleItem = cloneDeep(schedule)

		this.scheduleEditService.initialTemplateId = cloneDeep(scheduleItem.templateId)
		this.scheduleEditService.currentSchedule = scheduleItem

		return scheduleItem
	}

	private async loadScheduleDefaults (): Promise<void> {
		let scheduleSettings = await apiScheduleSettings.ScheduleSettingsList()
		if (scheduleSettings) {
			Vue.set(this.scheduleEditService, "scheduleSetting", scheduleSettings)
		}
	}

	private async deleteSchedule (schedule: Schedule): Promise<void> {
		await this.scheduleEditService.deleteSchedule(schedule)
		await this.loadSchedules()
	}

	private async saveSchedule (schedule: Schedule): Promise<void> {
		await this.scheduleEditService.saveSchedule(schedule)
		await this.loadSchedules()
	}

	private createSchedule (values: any) {
		const ianaTimezoneValue = Intl.DateTimeFormat().resolvedOptions().timeZone
		const timeZone = this.timeZones.find(t => t.iana === ianaTimezoneValue)

		var newSchedule = {
			timeZoneId: timeZone ? timeZone.id : null,
			fields: {},
			folder: (!!values && values.folderTitle && values.folderTitle !== "Uncategorized") ? values.folderTitle : null,
			templateId: null,
			...(values || {})
		}
		this.scheduleEditService.initialTemplateId = null
		this.scheduleEditService.currentSchedule = newSchedule

		return newSchedule
	}

	private getScheduleFrequencyDesc (schedule: Schedule): string {
		if (!schedule || schedule.scheduleFreq === null || schedule.scheduleFreq === undefined) return ""

		const selectedFrequency = this.scheduleFreqOptions.filter(x => x.id === schedule.scheduleFreq)[0]
		if (!selectedFrequency) {
			return ""
		}

		switch (schedule.scheduleFreq) {
			case ScheduleFreq.Daily:
				return selectedFrequency.title
			case ScheduleFreq.Weekly:
				const baseDate = new Date(2017, 0, 2)
				baseDate.setDate(baseDate.getDate() + schedule.dayOf! - 1)
				return selectedFrequency.title + " (" + baseDate.toLocaleDateString(window.navigator.language, { weekday: "long" }) + ")"
			case ScheduleFreq.Monthly:
				return selectedFrequency.title + " (" + schedule.dayOf! + ")"
			case ScheduleFreq.Once:
				return new Date(schedule.date!).toDateString()
		}

		return selectedFrequency.title
	}

	private getStatusDescription (schedule: Schedule): string {
		if (!schedule || schedule.status === null || schedule.status === undefined) return ""

		return ScheduleStatus[schedule.status]
	}

	@Watch("scheduleEditService.currentSchedule.templateId")
	private templateChanged (selectedTemplateId: string) {
		var applyDefaultValues = selectedTemplateId !== this.scheduleEditService.initialTemplateId

		this.scheduleEditService.templateChanged(selectedTemplateId, applyDefaultValues)
		this.scheduleEditService.initialTemplateId = selectedTemplateId
	}

	public saveSelectedFields (event: any) {
		this.selectedFields = event
	}

	private async TriggerGeneration (schedule: any) {
		try {
			this.generatingCase = true
			const createdCases = await api.generateCaseFromSchedule(schedule)

			createdCases.forEach(createdCase => {
				this.$bvToast.toast(`Created ${createdCase.caseNumber}`, {
					title: "Created",
					variant: "success",
					solid: true,
					autoHideDelay: 5000
				})
			})
		} catch (error) {
			console.log(error)
			this.$bvToast.toast("There was a problem triggering the schedule", {
				title: "Schedule Error",
				variant: "danger",
				solid: true,
				autoHideDelay: 5000
			})
		} finally {
			// wait 2 seconds before enabling the trigger again
			setTimeout(() => { this.generatingCase = false }, 2000)
		}
	}

	private toggleExpandFolder (folder: any) {
		if (!folder.isFolderType) {
			return
		}
		const folderRow: FolderSchedules = folder
		folderRow.expanded = !folderRow.expanded
		this.folders[folderRow.folderTitle].expanded = folderRow.expanded
	}

	public get expandedRows () : (FolderSchedules | Schedule)[] {
		const expandedRows = _(this.folderScheduleGroups)
			.orderBy([(item: FolderSchedules) => { return item.folderTitle }])
			.map(folderGroup => {
				return folderGroup.expanded
					? [folderGroup, ..._(folderGroup.schedules).orderBy(this.getScheduleOrdering, this.getScheduleOrderDirection).value()]
					: [folderGroup]
			}).flatten()
			.value()

		return expandedRows
	}

	public get getScheduleOrdering () : ((item: Schedule) => any)[] {
		return this.sortByOptions.map((sortByOption) => {
			return (item: Schedule) => { return get(item, sortByOption.columnKey, 0) }
		})
	}

	public get getScheduleOrderDirection () : ("asc" | "desc")[] {
		return this.sortByOptions.map((sortByOption) => {
			return sortByOption.descending
		})
	}

	private sortChanged (context: any) {
		if (!context || !context.sortBy) {
			this.sortByOptions = this.getDefaultSortByOptions()
		}

		const descOption = context.sortDesc ? "desc" : "asc"

		this.sortByOptions = [{
			columnKey: context.sortBy,
			descending: descOption
		}]
	}

	private rowClass (item: any, type: any): string {
		if (!item || type !== "row") return ""
		if (item.isFolderType) return "folder-row"

		const schedule = item as Schedule

		if (schedule.status === ScheduleStatus.Closed) {
			return "muted-row"
		}
		return ""
	}

	private determineScheduleStatus (schedule: Schedule) : ScheduleStatus {
		if (schedule.scheduleFreq === ScheduleFreq.Once && moment().utc().startOf("day") < moment(schedule.date)) {
			return ScheduleStatus.Upcoming
		}

		if (schedule.minRunDate && moment().utc().startOf("day") < moment(schedule.minRunDate)) {
			return ScheduleStatus.Upcoming
		}

		if (schedule.maxRunDate && moment().utc().startOf("day") > moment(schedule.maxRunDate)) {
			return ScheduleStatus.Closed
		}
		return ScheduleStatus.Open
	}

	private createScheduleFromRow (item: any) {
		this.$refs.dataTable.createNew(item)
	}

	private friendlyFolderName (schedule: Schedule) {
		if (schedule.folder === null || schedule.folder === undefined) {
			return "None"
		}
		return schedule.folder
	}
}

